import React, { useState } from "react";
import api from "../api/api";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Avatar,
  IconButton,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import defaultAvatar from "./blueavartar.png";

const Register = () => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirm_password: "",
    user_type: "",
    gender: "",
    profile_image: null,
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({ ...prev, profile_image: file }));
    if (file) {
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const validateForm = () => {
    const validationErrors = {};
    if (!formData.username) validationErrors.username = "Username is required.";
    if (!formData.email) validationErrors.email = "Email is required.";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      validationErrors.email = "Email is invalid.";
    if (!formData.password) validationErrors.password = "Password is required.";
    else if (formData.password.length < 8)
      validationErrors.password = "Password must be at least 8 characters.";
    if (!formData.confirm_password)
      validationErrors.confirm_password = "Confirm password is required.";
    else if (formData.password !== formData.confirm_password)
      validationErrors.confirm_password = "Passwords do not match.";
    if (!formData.user_type) validationErrors.user_type = "User role is required.";
    if (!formData.gender) validationErrors.gender = "Gender is required.";
    return validationErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setApiError("");
    setSuccess("");
    setLoading(true);

    const data = new FormData();
    // Send all fields including confirm_password
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== null) {
        data.append(key, value);
      }
    });

    try {
      const access = localStorage.getItem("access");
      const response = await api.post("/register/", data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: access ? `Bearer ${access}` : undefined,
        },
      });

      setLoading(false);
      setSuccess(response.data.message || "User added successfully!");
      setFormData({
        username: "",
        email: "",
        password: "",
        confirm_password: "",
        user_type: "",
        gender: "",
        profile_image: null,
      });
      setImagePreview(null);
    } catch (error) {
      setLoading(false);
      console.log("Backend error:", error.response?.data);
      const backendErrors = error.response?.data || {};
      setApiError(backendErrors.detail || "An error occurred.");
      setErrors(backendErrors);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      encType="multipart/form-data"
      sx={{
        maxWidth: 1100,
        mx: "auto",
        mt: 4,
        p: 4,
        border: "1px solid #ccc",
        borderRadius: 2,
        bgcolor: "background.paper",
      }}
    >
      <Typography variant="h4" textAlign="center" gutterBottom>
        Add User
      </Typography>

      {apiError && (
        <Box sx={{ bgcolor: "#ffcccc", p: 2, borderRadius: 1, mb: 2, textAlign: "center" }}>
          <Typography color="error" variant="body2">
            {apiError}
          </Typography>
        </Box>
      )}
      {success && (
        <Box sx={{ bgcolor: "#d0ebff", p: 2, borderRadius: 1, mb: 2, textAlign: "center" }}>
          <Typography color="primary" variant="body2">
            {success}
          </Typography>
        </Box>
      )}

      <Grid container spacing={2}>
        {/* Avatar + Upload */}
        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
        >
          <Avatar
            src={imagePreview || defaultAvatar}
            alt="Profile preview"
            sx={{ width: 170, height: 170, mb: 2 }}
            variant="rounded"
          />
          <Button component="label" variant="outlined">
            Upload Image
            <input
              type="file"
              hidden
              name="profile_image"
              accept="image/*"
              onChange={handleFileChange}
            />
          </Button>
          {errors.profile_image && (
            <Typography color="error" variant="caption">
              {errors.profile_image}
            </Typography>
          )}
        </Grid>

        {/* Form Fields */}
        <Grid item xs={12} sm={6} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                error={!!errors.username}
                helperText={errors.username}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email} 
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((p) => !p)}
                        edge="end"
                        aria-label="toggle password"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleChange}
                error={!!errors.confirm_password}
                helperText={errors.confirm_password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword((p) => !p)}
                        edge="end"
                        aria-label="toggle confirm password"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.user_type}>
                <InputLabel id="role-label">Role</InputLabel>
                <Select
                  labelId="role-label"
                  name="user_type"
                  value={formData.user_type}
                  label="Role"
                  onChange={handleChange}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {/* <MenuItem value="admin">Admin</MenuItem> */}
                  <MenuItem value="principal">Principal</MenuItem>
                  <MenuItem value="registry">Registry</MenuItem>
                  <MenuItem value="fiscal affairs">V.P Fiscal Affair</MenuItem>
                  <MenuItem value="vpi">VPI</MenuItem>
                  <MenuItem value="vpa">VPA</MenuItem>
                  <MenuItem value="it personel">IT Personel - Not Active Yet</MenuItem>
                  <MenuItem value="teacher">Teacher</MenuItem>
                  <MenuItem value="parent">Parent</MenuItem>
                  <MenuItem value="student">Student</MenuItem>

                  {/* <MenuItem value="dean">Dean</MenuItem> */}
                </Select>
                {errors.user_type && (
                  <Typography color="error" variant="caption">
                    {errors.user_type}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.gender}>
                <InputLabel id="gender-label">Gender</InputLabel>
                <Select
                  labelId="gender-label"
                  name="gender"
                  value={formData.gender}
                  label="Gender"
                  onChange={handleChange}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
                {errors.gender && (
                  <Typography color="error" variant="caption">
                    {errors.gender}
                  </Typography>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        sx={{ mt: 4, height: 48 }}
        disabled={loading}
      >
        {loading ? <CircularProgress size={24} /> : "Add User"}
      </Button>
    </Box>
  );
};

export default Register;








